import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import styles from './ai-chat-assets-view.module.scss';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {AiChatGeneratedAsset} from '@AiChat/ai-chat.types';
import {AiChatGeneratedAssetFilter, AiChatGeneratedAssetType} from '@AiChat/ai-chat.types';
import {AiChatAssetsGrid} from './components/ai-chat-assets-grid/ai-chat-assets-grid';
import {AiChatAssetsPromptBox} from './components/ai-chat-assets-prompt-box/ai-chat-assets-prompt-box';
import {AiChatAssetsFilters} from './components/ai-chat-assets-filters/ai-chat-assets-filters';
import {AiChatAssetsImagePreview} from './components/ai-chat-assets-image-preview/ai-chat-assets-image-preview';
import {AiChatAssetsVideoPreview} from './components/ai-chat-assets-video-preview/ai-chat-assets-video-preview';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {useAiChatLayout} from '@AiChat/hooks/useAiChatLayout';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';

export function AiChatAssetsView(): ReactElement {
  const {state, actions} = useAiChat();
  const {isSmall} = useAiChatSize();
  const {isMobileLayout} = useAiChatLayout();
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState<AiChatGeneratedAssetFilter>(AiChatGeneratedAssetFilter.ALL);
  const [previewAsset, setPreviewAsset] = useState<AiChatGeneratedAsset | null>(null);
  const [showIntroSubtitle] = useState<boolean>(state.shouldShowAssetsIntroSubtitle ?? false);
  const hasHeroTitle = !state.isEmbedded || !isMobileLayout;

  useEffect(() => {
    if (showIntroSubtitle) {
      actions.consumeAssetsIntroSubtitleView();
    }
  }, [showIntroSubtitle, actions.consumeAssetsIntroSubtitleView]);

  const openPreview = (asset: AiChatGeneratedAsset): void => {
    trackGA4CustomEvent(GA4EventName.AICHAT_MY_AI_DESIGN_CLICKED);
    setPreviewAsset(asset);
  };

  const closePreview = (): void => {
    setPreviewAsset(null);
  };

  const getTopPaddingClass = (): string => {
    if (!hasHeroTitle && isSmall) {
      return 'spacing-p-t-5';
    }

    if (isSmall || !hasHeroTitle) {
      return 'spacing-p-t-8';
    }

    return 'spacing-p-t-20';
  };

  const getContainerPaddingClasses = (): string => {
    if (isSmall) {
      return `${getTopPaddingClass()} spacing-p-b-4 spacing-p-l-5 spacing-p-r-5`;
    }

    return `${getTopPaddingClass()} spacing-p-b-8 spacing-p-l-20 spacing-p-r-20`;
  };

  const getTitleClasses = (): string => {
    if (isSmall) {
      return `${styles.title} ${styles.titleSmall}`;
    }

    return styles.title;
  };

  const getContentClasses = (): string => {
    if (isSmall) {
      return `${styles.content} ${styles.contentSmall}`;
    }

    return styles.content;
  };

  const renderSubtitle = (): ReactElement | null => {
    if (!showIntroSubtitle) {
      return null;
    }

    return <Text className={`${styles.subtitle} _unmargin`} size={TextSize.MD} val={window.i18next.t('pmwjs_my_ai_designs_subtitle')} />;
  };

  const renderTitle = (): ReactElement | null => {
    if (!hasHeroTitle) {
      return null;
    }

    return (
      <div className="flex-h-row flex-center gap-2">
        <Text className={`${getTitleClasses()} _unmargin`} val={window.i18next.t('pmwjs_my_ai_designs')} />
        <Icon icon="icon-sparkle-filled" className="content-ai" size={IconSize.SIZE_ICON_32} />
      </div>
    );
  };

  const renderTitleGroup = (): ReactElement | null => {
    if (!hasHeroTitle && !showIntroSubtitle) {
      return null;
    }

    return (
      <div className={styles.titleGroup}>
        {renderTitle()}
        {renderSubtitle()}
      </div>
    );
  };

  const renderHeader = (): ReactElement => {
    return (
      <div className={styles.header}>
        {renderTitleGroup()}
        <AiChatAssetsPromptBox />
      </div>
    );
  };

  const renderAssetsSection = (): ReactElement => {
    return (
      <div className={styles.assetsSection}>
        <AiChatAssetsFilters filter={filter} onFilterChange={setFilter} />
        <AiChatAssetsGrid filter={filter} scrollingContainer={scrollContainer} onAssetClick={openPreview} />
      </div>
    );
  };

  const renderPreview = (): ReactElement | null => {
    if (!previewAsset) {
      return null;
    }

    if (previewAsset.type === AiChatGeneratedAssetType.VIDEO) {
      return <AiChatAssetsVideoPreview asset={previewAsset} onClose={closePreview} />;
    }

    return <AiChatAssetsImagePreview asset={previewAsset} onClose={closePreview} />;
  };

  return (
    <div ref={setScrollContainer} className={`flex-v-row _full-height _full-width ${styles.container} ${getContainerPaddingClasses()}`}>
      <div className={getContentClasses()}>
        {renderHeader()}
        {renderAssetsSection()}
      </div>
      {renderPreview()}
    </div>
  );
}
